

import { debounce } from "lodash";
import { Vue, Component, Watch, Ref, Prop } from "vue-property-decorator";
import AccessorialChargesModal from "@/components/widgets/Modals/AccessorialChargesModal.vue";
import { formatDateWithTime, formatDate } from "../../utils";
import { getModule } from "vuex-module-decorators";
import OrderModule from "@/store/modules/order";
import SystemModule from "@/store/modules/system";
import TruckModule from "@/store/modules/truck";
import { OrderTypeToDeliveryTypeMappingModule } from "@/store/modules";
import moment from "moment";
import { IOrderTypeToDeliveryTypeMapping, IOrderResource } from "@/store/types";
import notifications from "@/lib/notifications";
import OrderDeliveryProfileWidget from "@/components/order/OrderDeliveryProfileWidget.vue";

const orderModule = getModule(OrderModule);
const systemModule = getModule(SystemModule);
const truckModule = getModule(TruckModule);

@Component({
  components: {
    AccessorialChargesModal,
    OrderDeliveryProfileWidget
  }
})
export default class OrderHeader extends Vue {
  availableManufacturers: Array<any> = null;
  debounceTime: number = 600;
  orderTypeToDeliveryTypeMappings: Array<IOrderTypeToDeliveryTypeMapping> = [];

  @Prop({ default: 0 })
  deliveryForId: number;

  @Ref() readonly accessorialsModal!: AccessorialChargesModal;

  @Watch("billToId")
  billToChange(newValue) {
    this.debounceBillToChange(newValue);
  }

  public async created() {
    this.billToChange(this.billToId);
    this.debounceBillToChange(this.billToId);

    const orderTypeToDeliveryTypeMappingModule = getModule(OrderTypeToDeliveryTypeMappingModule);
    this.orderTypeToDeliveryTypeMappings = await orderTypeToDeliveryTypeMappingModule.getOrderTypeToDeliveryTypeMapping();

    orderModule.enforceHomeCarrier();
  }

  get currentOrder(): IOrderResource {
    return orderModule.currentOrder;
  }

  get trackingNumber(): string {
    const orderWithTracking = this.currentOrder as IOrderResource & {
      trackingNumber?: string | null;
      TrackingNumber?: string | null;
    };
    return (orderWithTracking.trackingNumber || orderWithTracking.TrackingNumber || "").trim();
  }

  get hasTrackingNumber(): boolean {
    return !!this.formattedTrackingNumber;
  }

  get formattedTrackingNumber(): string {
    const cleanTrackingNumber = this.trackingNumber
      ? this.trackingNumber.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
      : "";

    if (!cleanTrackingNumber) return "";
    if (cleanTrackingNumber.length <= 2) return cleanTrackingNumber;

    const prefix = cleanTrackingNumber.substring(0, 2);
    const tail = cleanTrackingNumber.substring(2).match(/.{1,4}/g) || [];
    return `${prefix} ${tail.join(" ")}`.trim();
  }

  get trackingNumberUrl(): string {
    const orderWithTrackingUrl = this.currentOrder as IOrderResource & {
      trackingNumberUrl?: string | null;
      TrackingNumberUrl?: string | null;
    };
    return orderWithTrackingUrl.trackingNumberUrl || orderWithTrackingUrl.TrackingNumberUrl || "";
  }

  get receivedDateTimeFormatted(): string {
    return orderModule.currentOrder
      ? formatDateWithTime(orderModule.currentOrder.anticipatedReceiveDate)
      : "";
  }

  set receivedDateTimeFormatted(newVal) {
    formatDateWithTime(newVal);
  }

  get actualReceiveDateFormatted(): string {
    return orderModule.currentOrder
      ? formatDateWithTime(orderModule.currentOrder.actualReceiveDate)
      : "";
  }

  set actualReceiveDateFormatted(newVal) {
    formatDateWithTime(new Date(newVal));
  }

  get actualDeliveryDateFormatted(): string {
    return orderModule.currentOrder
      ? formatDateWithTime(orderModule.currentOrder.actualDeliveryDate)
      : "";
  }

  set actualDeliveryDateFormatted(newVal) {
    formatDateWithTime(newVal);
  }

  get hasShipsWithOrders(): any {
    return orderModule.currentOrder.hasShipsWithOrders;
  }

  get billToId(): any {
    return orderModule.currentOrder.billTo ? orderModule.currentOrder.billTo.displayId : null;
  }

  set billToId(value: any) {
    orderModule.updateBillTo(value);
  }

  get carrier(): string {
    return orderModule.currentOrder.carrier;
  }

  set carrier(value: string) {
    orderModule.updateCarrier(value);
    orderModule.enforceHomeCarrier();
  }

  get isCarrierEnabled() : boolean {
    return orderModule.isCarrierEnabled;
  }

  get crn(): string {
    return orderModule.currentOrder.crn;
  }

  set crn(value: string) {
    orderModule.updateCrn(value);
  }

  get codPending(): boolean {
    return orderModule.currentOrder.codPending;
  }

  set codPending(value: boolean) {
    orderModule.updateCodPending(value);
  }

  get isAsapDelivery(): boolean {
    return orderModule.currentOrder.isAsapDelivery;
  }

  set isAsapDelivery(value: boolean) {
    orderModule.updateIsAsapDelivery(value);
  }

  get customerNotReady(): boolean {
    return orderModule.currentOrder.customerNotReady;
  }

  get shipDate(): Date | string {
    return orderModule.currentOrder.shipDate;
  }

  set shipDate(value: Date | string) {
    orderModule.updateShipDate(value);
  }

  get anticipatedReceiveDate(): Date | string {
    return orderModule.currentOrder.anticipatedReceiveDate;
  }

  set anticipatedReceiveDate(value: Date | string) {
    orderModule.updateAnticipatedReceiveDate(value);
  }

  get requestedDeliveryDate(): Date | string {
    return orderModule.currentOrder.requestedDeliveryDate;
  }

  set requestedDeliveryDate(value: Date | string) {
    orderModule.updateRequestedDeliveryDate(value);
  }

  get followUpDate(): Date | string {
    return orderModule.currentOrder.followUpDate;
  }

  set followUpDate(value: Date | string) {
    orderModule.updateFollowUpDate(value);
  }

  get dockSearchDate(): Date | string {
    return orderModule.currentOrder.dockSearchDate;
  }

  set dockSearchDate(value: Date | string) {
    orderModule.updateDockSearchDate(value);
  }

  get facility(): string {
    return orderModule.currentOrder.facility;
  }

  set facility(value: string) {
    orderModule.updateFacility(value);
    orderModule.enforceHomeCarrier();
  }

  get manufacturer(): string {
    return orderModule.currentOrder.manufacturer;
  }
  set manufacturer(value: string) {
    orderModule.updateManufacturer(value);
  }

  get rateBy(): string {
    return orderModule.currentOrder.rateBy;
  }

  set rateBy(value: string) {
    orderModule.updateRateBy(value);
  }

  get orderType(): string {
    return orderModule.currentOrder.orderType;
  }

  set orderType(value: string) {
    orderModule.updateOrderType(value);
  }

  get status(): string {
    return orderModule.currentOrder.status;
  }

  set status(value: string) {
    orderModule.updateOrderStatus(value);
  }

  get deliveryType(): string {
    return orderModule.currentOrder.deliveryType;
  }

  set deliveryType(value: string) {
    var priorOrderType = orderModule.currentOrder.orderType;

    orderModule.updateDeliveryType(value);

    const map = this.orderTypeToDeliveryTypeMappings.find(x => x.deliveryType === value);
    const orderType = map ? map.orderType : "";

    orderModule.updateOrderType(orderType);
    orderModule.enforceHomeCarrier();

    if ((orderType === 'RET' || priorOrderType === 'RET') && orderType !== priorOrderType) {
      var shipper = { ...orderModule.currentOrder.consignee };
      var consignee = { ...orderModule.currentOrder.shipper };
      var payload = [{ type: 'shipper', party: shipper},{ type: 'consignee', party: consignee}];
      orderModule.changeCurrentOrderParties(payload);
      notifications.display('Address Switched', 'Shipper and Consignee have been switched.', 'info');
    }
  }

  get timeframeStart(): string {
    return moment(orderModule.currentOrder.timeframeStart).format("HH:mm");
  }

  get timeframeEnd(): string {
    return moment(orderModule.currentOrder.timeframeEnd).format("HH:mm");
  }

  get deliveryForName(): string {
    return orderModule.currentOrder.deliveryForName;
  }

  viewTruck()
  {
    if(orderModule.currentOrder.truckId)
      truckModule.viewTruckAction(orderModule.currentOrder.truckId)
  }

  showNotesIfAvailable() {
    if (this.currentOrder && this.currentOrder.hasNotes)
      return orderModule.notesShortcutAction(null);
  }

  debounceBillToChange(companyId: string) {
    return debounce(async () => await this.asyncBillToChange(companyId), this.debounceTime);
  }

  get payPeriodFormatted() {
    return orderModule.currentOrder
      ? formatDate(orderModule.currentOrder.payPeriod)
      : "";
  }
  set payPeriodFormatted(newVal) {
    formatDate(newVal);
  }

  async asyncBillToChange(companyId: string) {
    let manufacturerResults = await systemModule.searchManufacturersByName({
      billTo: companyId,
      name: ""
    });

    this.availableManufacturers = manufacturerResults.map(x => ({
      label: x.name,
      value: x.id
    }));

    if (this.availableManufacturers && this.availableManufacturers.length == 1)
      this.manufacturer = this.availableManufacturers[0].value;
    else if (
      !this.availableManufacturers ||
      this.availableManufacturers.filter(x => x.value == this.manufacturer).length == 0
    )
      this.manufacturer = null;
  }

  showAccessorials() {
    this.accessorialsModal.show();
  }

  copyTrackingNumber() {
    navigator.clipboard.writeText(this.trackingNumber);
    notifications.success("Copied", "Tracking # copied to your clipboard.");
  }

  openOrderInNewTab() {
    if (!this.trackingNumberUrl) {
      notifications.error("Tracking Link Unavailable", "No tracking URL is available for this order.");
      return;
    }
    window.open(this.trackingNumberUrl, "_blank");
  }
}
